import { useKeycloak } from '@react-keycloak/web'
import arrayMutators from 'final-form-arrays'
import { useEffect, useMemo, useState } from 'react'
import { Form } from 'react-final-form'
import { connect } from 'react-redux'
import { getCompanies } from '../../api/company'
import { invitingUsers } from '../../api/userCompany'
import { admin_user_role } from '../../config/config'
import AddingUserForm from '../../screens/Admin/forms/AddingUserForm'
import { isAuthorized } from '../../utils/security'
import { trans } from '../../utils/utilsTrans'
import AvatarCompany from '../UI/Avatars/AvatarCompany'
import AvatarUser from '../UI/Avatars/AvatarUser'
import { SecondaryButton } from '../UI/Buttons/Button'
import Modal from '../UI/Modal'
import Table from '../UI/Table'

const steps = [
  {
    title: 'users.steps.1.title',
    subtitle: null,
    icon: 'icon-user-add',
  },
  {
    title: 'users.steps.2.title',
    subtitle: 'users.steps.2.subtitle',
    icon: 'icon-user-add',
  },
  {
    title: 'users.steps.3.title',
    subtitle: 'users.steps.3.subtitle',
    icon: 'icon-annotation-check',
    isSuccess: true,
  },
]

const InvitingModal = ({
  onClose,
  userEmail,
  company,
  isAdmin,
  companyHeadId
}) => {
  const { keycloak } = useKeycloak()

  const isSuperAdmin = useMemo(
    () => isAuthorized(keycloak, [admin_user_role]),
    [keycloak]
  )

  const [query, setQuery] = useState('')
  const [companies, setCompanies] = useState([])
  const [searchTimeout, setSearchTimeout] = useState(null)
  const [loading, setLoading] = useState(true)
  const [companiesCount, setCompaniesCount] = useState(0)
  const [selectedCompany, setSelectedCompany] = useState(company)
  const [step, setStep] = useState(0)
  const [currentRole, setCurrentRole] = useState(isSuperAdmin ? 9 : 4)

  const columns = useMemo(
    () => [
      {
        Header: '',
        id: 'name',
        accessor: (data) => {
          return (
            <div className={'flex items-center space-x-8'}>
              <AvatarCompany name={data.name} />
              <span className='block text-greyLight font-medium mr-4 truncate'>
                {data.name}
              </span>
            </div>
          )
        },
      },
      {
        Header: '',
        id: 'members',
        accessor: (data) => (
          <div className='flex space-x-8 items-center'>
            <div className='flex space-x-[-10px] text-white'>
              {data.companyUsers.map((w) => (
                <AvatarUser
                  key={w.user?.id}
                  borders={true}
                  lastName={w.user?.lastname}
                  firstName={w.user?.firstname}
                />
              ))}
              {data.companyUsers.length > 5 ? (
                <div className='w-[36px] h-[36px] bg-primaryLight3 text-primary border-2 border-white rounded-full font-semibold flex items-center justify-center text-sm'>
                  +{data.extra.companyUsersCount / data.companyUsers.length}
                </div>
              ) : null}
            </div>
            <span className='block text-[13px] text-greyLight5 font-medium'>
              {data.extra.companyUsersCount}{' '}
              {data.extra.companyUsersCount >= 2
                ? trans('companies.collaborators')
                : trans('companies.collaborator')}
            </span>
          </div>
        ),
      },
      {
        id: 'action',
        Header: '',
        collapse: true,
        accessor: (data) => (
          <i className='icon-chevron-right text-2xl text-greyLight4' />
        ),
      },
    ],
    []
  )
  useEffect(() => {
    if (selectedCompany || isAdmin) {
      setStep(1)
    }
  }, [selectedCompany])

  useEffect(() => {
    if (!selectedCompany) {
      reloadList()
    }
  }, [])

  useEffect(() => {
    if ((query && query.length > 2) || (companiesCount && !query && !loading)) {
      setLoading(true)
      clearTimeout(searchTimeout)
      setSearchTimeout(
        setTimeout(() => {
          reloadList()
        }, 400)
      )
    }

    return () => clearTimeout(searchTimeout)
  }, [query])

  const reloadList = () => {
    const params = { search: { q: query } }
    getCompanies(params)
      .then((res) => {
        setLoading(false)
        setCompanies(res[0])
        setCompaniesCount(res[1])
      })
      .catch(() => {
        setLoading(false)
      })
  }

  const onSubmit = (values) => {
    const body = userEmail
      ? {
          invitations: [{ email: userEmail, role: currentRole }],
        }
      : values

    if (body?.invitations?.length >= 1) {
      invitingUsers(isAdmin ? companyHeadId : selectedCompany, body)
        .then(() => setStep(2))
        .catch((err) => console.error(err))
    }
  }

  return (
    <Modal
      type={step === 2 ? 'small' : 'semiMedium'}
      icon={steps[step].icon}
      success={steps[step].isSuccess}
      centeredHeader
      subTitle={trans(steps[step].subtitle)}
      title={trans(steps[step].title)}
      closeModal={onClose}
    >
      <Form
        onSubmit={onSubmit}
        initialValuesEqual={() => true} // Do not remove
        initialValues={{}}
        mutators={{
          ...arrayMutators,
        }}
        render={({ handleSubmit, values }) => (
          <form onSubmit={handleSubmit}>
            {step === 0 ? (
              <div className='p-24 min-h-[433px]'>
                <div className='px-32'>
                  <div className='flex space-x-16 items-center border-b border-greyLight7 py-16'>
                    <i className='icon-search-md text-xl text-greyLight3' />
                    <input
                      type={'text'}
                      className='w-full p-6 bg-transparent placeholder:text-greyLight3 font-medium text-sm'
                      placeholder={`${trans('companies.search')}...`}
                      value={query}
                      onChange={(e) => setQuery(e.target.value || '')}
                    />
                  </div>
                  {loading ? (
                    <div className='flex flex-col space-y-8 mt-16'>
                      {Array(4)
                        .fill('point')
                        .map((value, index) => (
                          <div
                            key={`${value}-${index}`}
                            className='rounded-8 bg-backgroundSecondary h-[70px] animate-pulse'
                          />
                        ))}
                    </div>
                  ) : (
                    <Table
                      emptyText={`${trans('no_result_for')} "${query}"`}
                      data={companies}
                      columns={columns}
                      total={companies.length}
                      onRowClick={(data) => {
                        setSelectedCompany(data.id)
                      }}
                      usePagination={false}
                      rowClass={'cursor-pointer hover:'}
                    />
                  )}
                </div>
              </div>
            ) : null}
            {step === 1 ? (
              <div className='p-24 px-40 pt-40 overflow-hidden flex flex-col'>
                <AddingUserForm
                  currentRole={currentRole}
                  setCurrentRole={setCurrentRole}
                  userEmail={userEmail}
                  values={values}
                  blackListRoles={isSuperAdmin ? [] : [9]}
                  selectedCompany={isAdmin ? companyHeadId : selectedCompany}
                />
              </div>
            ) : null}
            {step === 2 ? (
              <div className='p-24 pt-0 overflow-hidden flex flex-col'>
                <div className='px-32 mt-32 mb-16 flex justify-center'>
                  <SecondaryButton
                    text={trans('users.buttons.back_to_users')}
                    onClick={onClose}
                  />
                </div>
              </div>
            ) : null}
          </form>
        )}
      />
    </Modal>
  )
}

const mapStateToProps = (state) => {
  return {
    data: state.data,
    companyHeadId: state.auth.user.network?.companyHeadId,
  }
}

export default connect(mapStateToProps)(InvitingModal)
